/**
 * Human units. `Intl` always, never hand-rolled.
 *
 * ⚠️ CURRENCY COMES FROM THE PAYLOAD, NEVER FROM THE LOCALE.
 *    docs/contracts/conventions.md sends money as {amount: <centavos>, currency}
 *    precisely so the portal can format it. A Chilean customer who had shoes
 *    cleaned in São Paulo owes BRL, and their UI is in Spanish — locale decides
 *    the *separators and placement*, the payload decides the *currency*.
 *    Deriving one from the other silently misstates a price.
 *
 * ⚠️ AMOUNTS ARE MINOR UNITS, AND NOT EVERY CURRENCY HAS TWO OF THEM.
 *    CLP has zero decimal digits; BRL has two. Dividing by a hard-coded 100
 *    would render CLP 1.000 as CLP 10. We ask Intl how many digits the currency
 *    actually has.
 */
import { currentLocale, type Locale } from './index.ts';

export interface Money {
  amount: number;
  currency: string;
}

/** Intl formatters are expensive to construct; build each once. */
const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat>();

function memo<T extends Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat>(
  key: string,
  make: () => T,
): T {
  const hit = cache.get(key);
  if (hit) return hit as T;
  const made = make();
  cache.set(key, made);
  return made;
}

/** How many minor units this currency subdivides into (BRL 2, CLP 0, JPY 0). */
function minorUnitDigits(currency: string, locale: Locale): number {
  const resolved = memo(`digits:${locale}:${currency}`, () =>
    new Intl.NumberFormat(locale, { style: 'currency', currency }),
  ).resolvedOptions().maximumFractionDigits;
  // `?? 2` and not `?? 0`: two is the overwhelmingly common case, so an
  // unknown currency renders slightly oddly rather than inflating a price 100×.
  return resolved ?? 2;
}

/**
 * `{amount: 25900, currency: 'BRL'}` → `R$ 259,00` (pt-BR) / `$ 259,00` (es-CL)
 * `{amount: 1000,  currency: 'CLP'}` → `CLP 1.000`  — zero decimals, not 10.
 */
export function formatMoney(money: Money | null | undefined, locale = currentLocale()): string {
  if (!money) return '—';
  const digits = minorUnitDigits(money.currency, locale);
  const value = money.amount / 10 ** digits;
  return memo(`money:${locale}:${money.currency}`, () =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: money.currency }),
  ).format(value);
}

/** RFC 3339 or `YYYY-MM-DD` → `14/07/2026`. */
export function formatDate(iso: string | null | undefined, locale = currentLocale()): string {
  if (!iso) return '—';
  const d = parse(iso);
  if (!d) return '—';
  return memo(`date:${locale}`, () => new Intl.DateTimeFormat(locale, { dateStyle: 'short' })).format(d);
}

export function formatDateTime(iso: string | null | undefined, locale = currentLocale()): string {
  if (!iso) return '—';
  const d = parse(iso);
  if (!d) return '—';
  return memo(`datetime:${locale}`, () =>
    new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }),
  ).format(d);
}

/**
 * "há 2 horas" / "hace 2 horas". Used by the notification inbox.
 *
 * Picks the largest unit that still reads naturally, so a three-day-old item is
 * "3 days ago" rather than "72 hours ago".
 */
export function formatRelativeTime(iso: string | null | undefined, locale = currentLocale()): string {
  if (!iso) return '—';
  const d = parse(iso);
  if (!d) return '—';

  const seconds = Math.round((d.getTime() - Date.now()) / 1000);
  const abs = Math.abs(seconds);

  const rtf = memo(`rel:${locale}`, () => new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }));

  const steps: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];

  for (const [unit, size] of steps) {
    if (abs >= size) return rtf.format(Math.round(seconds / size), unit);
  }
  return rtf.format(seconds, 'second');
}

/** Never throw on malformed upstream data — a bad date must not blank a screen. */
function parse(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}
